<script setup>
import { computed, defineModel, h, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import FilterSelect from './inputs/FilterSelect.vue';
import MultiSelect from './inputs/MultiSelect.vue';
import SingleSelect from './inputs/SingleSelect.vue';

import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { validateSingleFilter } from 'dashboard/helper/validations.js';

const { filterTypes, isFirstQueryOperator } = defineProps({
  showQueryOperator: { type: Boolean, default: false },
  isFirstQueryOperator: { type: Boolean, default: false },
  filterTypes: { type: Array, required: true },
});

const emit = defineEmits(['remove']);

const DATE_ATTRIBUTES = ['created_at', 'last_activity_at'];

const { t } = useI18n();
const showErrors = ref(false);

const attributeKey = defineModel('attributeKey', {
  type: String,
  required: true,
});

const values = defineModel('values', {
  type: [String, Number, Array, Object],
  required: true,
});

const filterOperator = defineModel('filterOperator', {
  type: String,
  required: true,
});

const queryOperator = defineModel('queryOperator', {
  type: String,
  required: false,
  default: undefined,
  validator: value => ['and', 'or'].includes(value),
});

const getFilterFromFilterTypes = key =>
  filterTypes.find(filterObj => filterObj.attributeKey === key);

const currentFilter = computed(() =>
  getFilterFromFilterTypes(attributeKey.value)
);

const getOperator = (filter, selectedOperator) => {
  if (!filter || !filter.filterOperators) return null;

  const operatorFromOptions = filter.filterOperators.find(
    operator => operator.value === selectedOperator
  );

  if (!operatorFromOptions) {
    return filter.filterOperators[0];
  }

  return operatorFromOptions;
};

const currentOperator = computed(() => {
  if (!currentFilter.value) return null;
  return getOperator(currentFilter.value, filterOperator.value);
});

const getInputType = (operator, filter) => {
  if (!operator || !filter) return null;
  return operator.inputOverride ?? filter.inputType;
};

const inputType = computed(() => {
  if (!currentOperator.value || !currentFilter.value) return null;
  return getInputType(currentOperator.value, currentFilter.value);
});

const queryOperatorOptions = computed(() => {
  return [
    {
      label: t(`FILTER.QUERY_DROPDOWN_LABELS.AND`),
      value: 'and',
      icon: h('span', { class: 'i-lucide-ampersands !text-n-blue-text' }),
    },
    {
      label: t(`FILTER.QUERY_DROPDOWN_LABELS.OR`),
      value: 'or',
      icon: h('span', { class: 'i-woot-logic-or !text-n-blue-text' }),
    },
  ];
});

const booleanOptions = computed(() => [
  { id: true, name: t('FILTER.ATTRIBUTE_LABELS.TRUE') },
  { id: false, name: t('FILTER.ATTRIBUTE_LABELS.FALSE') },
]);

// Coluna numérica (e os overrides `number` de due_in_days/due_x_days_ago)
// rendem input nativo de número — teclado numérico no mobile e sem letra
// digitável indo virar bind de comparação `>=` contra coluna int.
const htmlInputType = computed(() => {
  if (inputType.value === 'date') return 'date';
  if (inputType.value === 'number') return 'number';
  return 'text';
});

const showTimeInput = computed(() => {
  if (!currentFilter.value || !currentOperator.value) return false;
  if (!DATE_ATTRIBUTES.includes(attributeKey.value)) return false;
  return inputType.value === 'date' && currentOperator.value.hasInput;
});

// Retorna o offset do timezone do browser em formato ±HH:MM (ex: "-03:00")
const getBrowserTimezoneOffset = () => {
  const offset = new Date().getTimezoneOffset(); // em minutos, positivo = west
  const sign = offset <= 0 ? '+' : '-';
  const absOffset = Math.abs(offset);
  const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
  const minutes = String(absOffset % 60).padStart(2, '0');
  return `${sign}${hours}:${minutes}`;
};

const datePartValue = computed(() => {
  const v = typeof values.value === 'string' ? values.value : '';
  return v.includes('T') ? v.split('T')[0] : v;
});

const timePartValue = computed(() => {
  const v = typeof values.value === 'string' ? values.value : '';
  if (!v.includes('T')) return '';
  // Remove o offset se existir (ex: "20:00-03:00" → "20:00")
  return v.split('T')[1].replace(/[+-]\d{2}:\d{2}$/, '');
});

const onDateChange = newDate => {
  const time = timePartValue.value;
  if (time) {
    values.value = `${newDate}T${time}${getBrowserTimezoneOffset()}`;
  } else {
    values.value = newDate;
  }
};

const onTimeChange = newTime => {
  const date = datePartValue.value;
  if (!date) return;
  if (newTime) {
    values.value = `${date}T${newTime}${getBrowserTimezoneOffset()}`;
  } else {
    values.value = date;
  }
};

const validationError = computed(() => {
  return validateSingleFilter(
    useSnakeCase({
      attributeKey: attributeKey.value,
      filterOperator: filterOperator.value,
      values: values.value,
    })
  );
});

const resetModelOnAttributeKeyChange = newAttributeKey => {
  const filter = getFilterFromFilterTypes(newAttributeKey);
  const newOperator = getOperator(filter, filterOperator.value);
  const newInputType = getInputType(newOperator, filter);
  if (newInputType === 'multiSelect') {
    values.value = [];
  } else if (['searchSelect', 'booleanSelect'].includes(newInputType)) {
    values.value = {};
  } else {
    values.value = '';
  }
  filterOperator.value = newOperator.value;
};

watch([attributeKey, values, filterOperator], () => {
  showErrors.value = false;
});

const validate = () => {
  showErrors.value = true;
  return !validationError.value;
};

defineExpose({ validate });
</script>

<template>
  <li class="list-none">
    <div
      class="flex items-center gap-2 rounded-md"
      :class="{
        'animate-wiggle': showErrors && validationError,
      }"
    >
      <!-- Dropdown editável apenas no primeiro, disabled nos demais -->
      <template v-if="showQueryOperator">
        <FilterSelect
          v-if="isFirstQueryOperator"
          v-model="queryOperator"
          variant="faded"
          hide-icon
          hide-option-icon
          class="text-sm"
          :options="queryOperatorOptions"
          :show-search="false"
          :show-groups="false"
        />
        <!-- Label estático para os demais (disabled) -->
        <span
          v-else
          class="px-2 py-1.5 text-sm text-n-slate-11 bg-n-slate-3 rounded-md min-w-[40px] text-center cursor-not-allowed opacity-60"
        >
          {{ queryOperator === 'or' ? 'OU' : 'E' }}
        </span>
      </template>

      <FilterSelect
        v-model="attributeKey"
        variant="faded"
        :options="filterTypes"
        :placeholder="t('FILTER.SELECT_FILTER_PLACEHOLDER')"
        show-search
        show-groups
        @update:model-value="resetModelOnAttributeKeyChange"
      />
      <FilterSelect
        v-if="currentFilter"
        v-model="filterOperator"
        variant="ghost"
        :options="currentFilter.filterOperators"
        :show-search="false"
        :show-groups="false"
      />
      <template
        v-if="currentFilter && currentOperator && currentOperator.hasInput"
      >
        <MultiSelect
          v-if="inputType === 'multiSelect'"
          v-model="values"
          :options="currentFilter.options"
        />
        <SingleSelect
          v-else-if="inputType === 'searchSelect'"
          v-model="values"
          :options="currentFilter.options"
        />
        <SingleSelect
          v-else-if="inputType === 'booleanSelect'"
          v-model="values"
          disable-search
          :options="booleanOptions"
        />
        <template v-else-if="showTimeInput">
          <Input
            :model-value="datePartValue"
            type="date"
            class="[&>input]:h-8 [&>input]:py-1.5 [&>input]:outline-offset-0"
            @update:model-value="onDateChange"
          />
          <Input
            :model-value="timePartValue"
            type="time"
            class="[&>input]:h-8 [&>input]:py-1.5 [&>input]:outline-offset-0 max-w-[120px]"
            :placeholder="t('FILTER.TIME_PLACEHOLDER')"
            @update:model-value="onTimeChange"
          />
        </template>
        <Input
          v-else
          v-model="values"
          :type="htmlInputType"
          class="[&>input]:h-8 [&>input]:py-1.5 [&>input]:outline-offset-0"
          :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
        />
      </template>
      <Button
        sm
        solid
        slate
        icon="i-lucide-trash"
        class="flex-shrink-0"
        @click.stop="emit('remove')"
      />
    </div>
    <span v-if="showErrors && validationError" class="text-sm text-n-ruby-11">
      {{ t(`FILTER.ERRORS.${validationError}`) }}
    </span>
  </li>
</template>
